import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileSpreadsheet, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useClientCSVImport } from '@/hooks/useClientCSVImport';
import { CSVDropzone } from '@/components/clients/import/CSVDropzone';
import { ColumnMappingDialog } from '@/components/clients/import/ColumnMappingDialog';
import { EditablePreviewTable } from '@/components/clients/import/EditablePreviewTable';
import { ImportProgressBar } from '@/components/clients/import/ImportProgressBar';
import { ImportResultsSummary } from '@/components/clients/import/ImportResultsSummary';
import { DownloadTemplateButton } from '@/components/clients/import/DownloadTemplateButton';

export default function ClientsImport() {
  const { t } = useTranslation(['clients', 'common']);
  const navigate = useNavigate();
  const { state, isLoading, reset, parseFile, setColumnMapping, startImport, updateClient, subscriptionTypes } = useClientCSVImport();
  
  // Count unmatched packs (clients with pack name but no matched type ID)
  const unmatchedPacksCount = state.clients.filter(c => c.subscriptionType && !c.subscriptionTypeId).length;

  const handleFileSelect = async (file: File) => {
    try {
      await parseFile(file);
    } catch (error) {
      console.error('Error parsing file:', error);
    }
  };

  const handleMappingComplete = async (mapping: any) => {
    await setColumnMapping(mapping);
  };

  const handleImport = async () => {
    await startImport();
  };

  const handleBackToClients = () => {
    navigate('/admin/clients');
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={handleBackToClients}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('import.title')}</h1>
          <p className="text-muted-foreground">{t('import.subtitle')}</p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        <StepIndicator 
          step={1} 
          label={t('import.steps.upload')} 
          isActive={state.step === 'upload'} 
          isComplete={state.step !== 'upload'} 
        />
        <div className="flex-1 h-px bg-border" />
        <StepIndicator 
          step={2} 
          label={t('import.steps.preview')} 
          isActive={state.step === 'mapping' || state.step === 'preview'} 
          isComplete={state.step === 'importing' || state.step === 'complete'} 
        />
        <div className="flex-1 h-px bg-border" />
        <StepIndicator 
          step={3} 
          label={t('import.steps.import')} 
          isActive={state.step === 'importing' || state.step === 'complete'} 
          isComplete={state.step === 'complete'} 
        />
      </div>

      {/* Content based on step */}
      {state.step === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {t('import.uploadTitle')}
            </CardTitle>
            <CardDescription>{t('import.uploadDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CSVDropzone onFileSelect={handleFileSelect} isLoading={isLoading} />
            <div className="flex justify-center">
              <DownloadTemplateButton />
            </div>
          </CardContent>
        </Card>
      )}

      {state.step === 'mapping' && (
        <ColumnMappingDialog
          headers={state.rawHeaders}
          currentMapping={state.columnMapping}
          onConfirm={handleMappingComplete}
          onCancel={reset}
          isLoading={isLoading}
        />
      )}

      {state.step === 'preview' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              {t('import.previewTitle')}
            </CardTitle>
            <CardDescription>
              {t('import.previewDescription', { total: state.clients.length })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Warning if packs not matched */}
            {unmatchedPacksCount > 0 && (
              <Alert variant="destructive" className="border-yellow-500 bg-yellow-50 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-400 dark:border-yellow-800">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t('import.warnings.unmatchedPacks', { count: unmatchedPacksCount })}
                  {subscriptionTypes.length > 0 && (
                    <span className="block mt-1 text-xs">
                      {t('import.warnings.availableTypes')}: {subscriptionTypes.map(t => t.name).join(', ')}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Stats summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard 
                label={t('import.stats.valid')} 
                value={state.validCount} 
                variant="success" 
              />
              <StatCard 
                label={t('import.stats.errors')} 
                value={state.errorCount} 
                variant="error" 
              />
              <StatCard 
                label={t('import.stats.duplicates')} 
                value={state.duplicateCount} 
                variant="warning" 
              />
              <StatCard 
                label={t('import.stats.existing')} 
                value={state.existingCount} 
                variant="info" 
              />
            </div>

            {/* Editable preview table */}
            <EditablePreviewTable 
              clients={state.clients} 
              onUpdateClient={updateClient}
            />

            {/* Actions */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={reset}>
                {t('common:cancel')}
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={state.validCount === 0}
              >
                {t('import.actions.import', { count: state.validCount })}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {state.step === 'importing' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('import.importing')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ImportProgressBar 
              progress={state.progress} 
              current={state.results.success + state.results.failed}
              total={state.validCount}
            />
          </CardContent>
        </Card>
      )}

      {state.step === 'complete' && (
        <ImportResultsSummary 
          results={state.results}
          onBackToClients={handleBackToClients}
          onImportMore={reset}
        />
      )}
    </div>
  );
}

function StepIndicator({ 
  step, 
  label, 
  isActive, 
  isComplete 
}: { 
  step: number; 
  label: string; 
  isActive: boolean; 
  isComplete: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className={`
        w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
        ${isComplete ? 'bg-primary text-primary-foreground' : 
          isActive ? 'bg-primary/20 text-primary border-2 border-primary' : 
          'bg-muted text-muted-foreground'}
      `}>
        {isComplete ? <Check className="h-4 w-4" /> : step}
      </div>
      <span className={`text-sm hidden sm:inline ${isActive ? 'font-medium' : 'text-muted-foreground'}`}>
        {label}
      </span>
    </div>
  );
}

function StatCard({ 
  label, 
  value, 
  variant 
}: { 
  label: string; 
  value: number; 
  variant: 'success' | 'error' | 'warning' | 'info';
}) {
  const variantStyles = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800',
    error: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800',
    warning: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-400 dark:border-yellow-800',
    info: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800',
  };

  return (
    <div className={`rounded-lg border p-3 ${variantStyles[variant]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}
